import React, { useState } from "react";
import { Mail, Copy, Check } from "lucide-react";
import { CONTACT } from "../../data/build";

// Two tuned variants — pick per recipient
export const OUTREACH = {
  biolab: {
    label: "Biolab / Bioguard",
    subject: "Aqualux Nexus × Bioguard — a strategic-alliance proposal (private preview)",
    body: `Hi Gene,

I'm ${CONTACT.name} from ${CONTACT.company} in ${CONTACT.location}. Thank you for taking the time — I'd genuinely value your read on this.

Over 15 years running a pool & spa retail business I built and lived in my own management software every day. I've now rebuilt that entire system as Aqualux Nexus: one live, cloud database that already carries 34,000+ invoices, 51,000+ quotes, 38,000+ work orders, 6,530 pools and 8,897 customers — migrated from the real business, not a mock-up. On top of that database I'm building a connected water-care platform: an in-store reagentless water tester, a mobile field tester, and a home test-and-dose automation unit that keeps a customer's water perfect and reorders chemistry automatically.

This is where Bioguard fits perfectly — your chemistry and testing science at the core, dosed and reordered automatically, with a live consumer database that keeps pool owners loyal to the brand. Plenty of pool companies have launched AI-built software this year; almost none own the trade data, the hardware roadmap and the home-automation path together. That's the gap I want to close with a partner like you.

I've prepared a private, NDA-protected proposal that walks through the platform, the software (with live screenshots) and exactly what a partnership could look like — strategic investment, Bioguard as the chemistry/brand partner, and manufacturing & distribution. You can view it here:

[PROPOSAL_LINK]
(You'll be asked to accept a short confidentiality agreement first — your access is logged for both our protection.)

Could we find 20 minutes for a call? I'm in ${CONTACT.location} and will happily work around your schedule.

Warm regards,
${CONTACT.name}
${CONTACT.phoneRaw}
${CONTACT.email}`,
  },
  manufacturer: {
    label: "Manufacturer",
    subject: "Prototype build partner — Aqualux Nexus pool-water tester (RFQ)",
    body: `Hi [Name],

I'm ${CONTACT.name}, founder of ${CONTACT.company}. We've designed a benchtop and portable pool-water tester — four continuous probes plus three optical test-strip slots, no liquid reagents — returning a full water-chemistry panel in about 60–90 seconds. It's aimed at pool shops, pool-service technicians and home users across a worldwide, multi-billion-dollar industry, and it syncs to our own pool-management software.

I'm looking for a product-build partner to create the first operational prototype and, over time, scale to full production. This is the first of a three-step product family — Step 1 the shop tester, Step 2 a mobile field tester once Step 1 is proven, and Step 3 an automated in-pool dosing system — so there's real room to grow together. I've prepared a detailed Manufacturing Brief / RFQ covering the specification, a 24-part component schedule, engineering drawings and exactly what I'd like quoted — happy to send it straight through.

Would you be open to a short feasibility call to discuss the concept and whether it's a fit for you? I'm based in ${CONTACT.location} and can work around your timezone.

Thanks very much,
${CONTACT.name}
${CONTACT.phoneRaw}
${CONTACT.email}`,
  },
  investor: {
    label: "Investor-style",
    subject: "Partnership opportunity — Aqualux Nexus smart pool-care platform",
    body: `Hi [Name],

I'm ${CONTACT.name}, founder of ${CONTACT.company}. Pool water care is a worldwide, multi-billion-dollar industry that's still confusing, manual and inconsistent — for pool shops, service technicians and everyday owners alike. Aqualux Nexus turns that into one connected, easy platform.

We start with a reagentless pool-water tester — four continuous probes and three optical strips returning a full water-chemistry panel in ~60–90 seconds — feeding our own pool-management software. It's the first of a three-step family: Step 1 the in-store shop tester, Step 2 a mobile field tester, and Step 3 an automated in-pool dosing system — a clear path from a single device to a recurring-revenue ecosystem.

I'm seeking a partner with a genuine vested interest to help take this from first prototype through to full production. I have a detailed brief, engineering drawings and a component-level build plan ready to share.

Could we set up a short call to explore the opportunity? I'm based in ${CONTACT.location} and happy to work around your timezone.

Kind regards,
${CONTACT.name}
${CONTACT.phoneRaw}
${CONTACT.email}`,
  },
};

export function OutreachEmail() {
  const [aud, setAud] = useState("manufacturer");
  const [copied, setCopied] = useState(false);
  const v = OUTREACH[aud];
  const full = `Subject: ${v.subject}\n\n${v.body}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
    } catch (e) {
      const ta = document.createElement("textarea");
      ta.value = full; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch (err) { console.error("Copy fallback failed:", err); }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="outreach-email">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="h-9 w-9 rounded-xl bg-sky-50 border border-sky-100 flex items-center justify-center"><Mail className="h-4 w-4 text-sky-600" /></span>
          <div>
            <p className="font-display font-bold text-slate-900">Outreach email — copy &amp; paste</p>
            <p className="text-xs text-slate-500">A ready first-contact message. Swap <span className="font-mono2">[Name]</span> for the firm, then paste into your email.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border border-slate-200 p-0.5 bg-slate-50">
            {Object.entries(OUTREACH).map(([k, o]) => (
              <button key={k} onClick={() => setAud(k)} data-testid={`email-tone-${k}`} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${aud === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>{o.label}</button>
            ))}
          </div>
          <button onClick={copy} data-testid="copy-email-btn" className={`inline-flex items-center gap-2 rounded-full text-sm font-semibold px-4 py-2 transition-colors ${copied ? "bg-emerald-600 text-white" : "bg-sky-600 hover:bg-sky-500 text-white"}`}>
            {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy email</>}
          </button>
        </div>
      </div>
      <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-4">
        <p className="font-mono2 text-[11px] uppercase tracking-widest text-slate-400 mb-1">Subject</p>
        <p className="text-sm text-slate-800 font-medium" data-testid="email-subject">{v.subject}</p>
        <p className="font-mono2 text-[11px] uppercase tracking-widest text-slate-400 mt-3 mb-1">Body</p>
        <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed" data-testid="email-body">{v.body}</pre>
      </div>
    </div>
  );
}
